/**
 * Live per-port shutter + FOUP transfer visuals for kit equipment.
 * Driven by OHT PICKUP/DROP dwells — same clock as stocker / OHT lifter.
 */
import { useMemo, useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import type { LayoutElement } from '../../../../types';
import { useAppSelector } from '../../../../hooks/redux';
import { replaySnapshotStore } from '../../../../store/replaySnapshotStore';
import type { EquipmentStatus } from '../types';
import { resolveKitFoupScale } from '../types';
import {
  getProcessEquipmentPortPoses,
  getStandaloneLoadPortPoses,
  resolveActiveEquipmentPortIndex,
  resolveEquipmentProcessStatus,
  resolveEquipmentTransferVisuals,
  type EquipmentPortVisual,
  type Vec3,
} from './equipmentPortTransfer';
import {
  getFabProcessToolPortPoses,
  resolveFabToolLayout,
} from './FabProcessTool/fabToolLayout';
import {
  buildElementPortOwnership,
  dockFractionsAlongFace,
  equipmentIdFromPortId,
  ownershipMatchesEquipmentId,
  portsOf,
  resolveOwnedPortIndex,
} from './equipmentPortOwnership';
import { worldPointToEquipmentKitLocal } from './equipmentFacing3d';
import { liveOhtRuntimeStore } from '../../../../store/liveOhtRuntimeStore';
import {
  resolveEquipmentFoupCapacity,
  resolveEquipmentFoupSlots,
  type EquipmentFoupSlotView,
} from './equipmentFoupSlots';
import {
  resolveFabChamberFoups,
  type ChamberFoupVisual,
} from './FabProcessTool/fabProcessTransferVisuals';

export type EquipmentFoupPresence = {
  foupPresent: boolean;
  occupiedLoadPorts: number[];
};

export type EquipmentPortTransferPresence = EquipmentFoupPresence & {
  portVisuals: EquipmentPortVisual[];
  /** Single flying FOUP in equipment-local space (never duplicated). */
  flyingFoup: { position: Vec3; scaleHint: number; foupId?: string } | null;
  activePortIndex: number | null;
  isTransferring: boolean;
  /** Normalized dwell progress for the active OHT handoff (NewEquipment pose sync). */
  transferProgress01: number | null;
  /** Active handoff kind when {@link transferProgress01} is set. */
  transferOperation: 'PICKUP' | 'DROP' | null;
  /**
   * True while a FOUP is inside the tool for processing (after drop until
   * pickup removes it).
   */
  isProcessing: boolean;
  /** Stack / LED status derived from FOUP location. */
  equipmentStatus: EquipmentStatus;
  /** Capacity-driven FOUP slot occupancy (socket-authoritative). */
  foupSlotView: EquipmentFoupSlotView;
  /** FOUPs resting in the upper chamber for 3D/2D rendering. */
  chamberFoups: ChamberFoupVisual[];
  foupCapacity: number;
};

function emptyVisuals(count: number): EquipmentPortVisual[] {
  return Array.from({ length: Math.max(1, count) }, (_, i) => ({
    portIndex: i,
    shutterOpen: 0,
    shutterState: 'closed' as const,
    transferState: 'idle' as const,
    occupancy: 'empty' as const,
    foupMode: 'hidden' as const,
    foupPosition: null,
    isLocked: false,
  }));
}

function processingRestVisuals(
  poses: ReturnType<typeof getProcessEquipmentPortPoses>,
  portIndex: number,
  foupId?: string
): EquipmentPortVisual[] {
  return poses.map((pose, i) => {
    if (i !== portIndex) {
      return {
        portIndex: i,
        shutterOpen: 0,
        shutterState: 'closed' as const,
        transferState: 'idle' as const,
        occupancy: 'empty' as const,
        foupMode: 'hidden' as const,
        foupPosition: null,
        isLocked: false,
      };
    }
    return {
      portIndex: i,
      shutterOpen: 0,
      shutterState: 'closed' as const,
      transferState: 'idle' as const,
      occupancy: 'occupied' as const,
      foupMode: 'resting' as const,
      foupPosition: pose.internal,
      isLocked: false,
      foupId,
    };
  });
}

const EMPTY_SLOT_VIEW: EquipmentFoupSlotView = {
  capacity: 1,
  occupied: 0,
  chamber: [],
  loadPortIndices: [],
};

const EMPTY: EquipmentPortTransferPresence = {
  foupPresent: false,
  occupiedLoadPorts: [],
  portVisuals: [],
  flyingFoup: null,
  activePortIndex: null,
  isTransferring: false,
  transferProgress01: null,
  transferOperation: null,
  isProcessing: false,
  equipmentStatus: 'IDLE',
  foupSlotView: EMPTY_SLOT_VIEW,
  chamberFoups: [],
  foupCapacity: 1,
};

function replayEquipmentStatus(status: string | undefined): EquipmentStatus {
  switch (status?.trim().toLowerCase()) {
    case 'busy':
    case 'occupy':
    case 'occupied':
      return 'RUNNING';
    case 'error':
      return 'ERROR';
    case 'offline':
      return 'OFFLINE';
    case 'ready':
    case 'available':
    default:
      return 'IDLE';
  }
}

export type UseEquipmentPortTransferOpts = {
  element?: LayoutElement | null;
  width: number;
  height: number;
  depth: number;
  loadPortCount?: number;
  /** Use standalone LoadPort geometry instead of ProcessEquipment. */
  standaloneLoadPort?: boolean;
  /** Reference FabProcessEquipment kit poses + upper-chamber lifter path. */
  fabProcessTool?: boolean;
};

/**
 * Subscribe to sim clock and drive per-port shutter + FOUP path for this tool.
 */
export function useEquipmentPortTransfer(
  opts: UseEquipmentPortTransferOpts
): EquipmentPortTransferPresence {
  const {
    element,
    width,
    height,
    depth,
    loadPortCount = 2,
    standaloneLoadPort = false,
    fabProcessTool = false,
  } = opts;

  const portCount = standaloneLoadPort
    ? 1
    : Math.max(1, Math.min(6, Math.round(loadPortCount)));

  const [presence, setPresence] = useState<EquipmentPortTransferPresence>(() => ({
    ...EMPTY,
    portVisuals: emptyVisuals(portCount),
  }));
  const lastKeyRef = useRef('');
  /** Last transfer already reported to the console (one line per operation). */
  const loggedTransferRef = useRef('');
  /** True while shutter/FOUP animation needs per-frame presence updates. */
  const presenceAnimatingRef = useRef(false);

  const running = useAppSelector((s) => s.simulation.running);
  const source = useAppSelector((s) => s.simulation.source);
  const simTimeS = useAppSelector((s) => s.simulation.simulationTime);
  const toolPickupReady = useAppSelector((s) => s.simulation.live.toolPickupReady);
  const equipmentInstances = useAppSelector((s) => s.equipment.instances);
  const instanceCapacity = element?.id
    ? equipmentInstances[element.id]?.foupCapacity
    : undefined;
  const foupCapacity = resolveEquipmentFoupCapacity(element, portCount, instanceCapacity);
  const fabLayout = useMemo(
    () => (fabProcessTool ? resolveFabToolLayout(width, height, depth) : null),
    [fabProcessTool, width, height, depth]
  );
  const legsByVehicle = useAppSelector(
    (s) => s.simulation.browserKinetic?.vehicleLegs.legsByVehicle
  );

  const ownership = useMemo(
    () => buildElementPortOwnership(element, equipmentInstances),
    [element, equipmentInstances]
  );

  const poses = useMemo(() => {
    const ports = element ? portsOf(element, equipmentInstances) : [];
    const fractions = dockFractionsAlongFace(
      ports,
      standaloneLoadPort ? 1 : portCount
    );
    // Both kits key their seats off the 2D port fractions, so the animated
    // handoff lands on the same spot the tray is drawn at.
    if (standaloneLoadPort) {
      return getStandaloneLoadPortPoses(width, height, depth, fractions);
    }
    if (fabProcessTool) {
      return getFabProcessToolPortPoses(
        resolveFabToolLayout(width, height, depth),
        portCount,
        fractions
      );
    }
    return getProcessEquipmentPortPoses(width, height, depth, portCount, fractions);
  }, [
    fabProcessTool,
    standaloneLoadPort,
    width,
    height,
    depth,
    portCount,
    element,
    equipmentInstances,
  ]);

  useFrame(() => {
    if (!running || !element) {
      const idleKey = `idle:${portCount}`;
      if (lastKeyRef.current !== idleKey) {
        lastKeyRef.current = idleKey;
        setPresence({
          ...EMPTY,
          portVisuals: emptyVisuals(portCount),
        });
      }
      return;
    }

    const snapshot = replaySnapshotStore.getSnapshot();
    const liveFrame = source === 'live' ? liveOhtRuntimeStore.getFrame() : null;
    const station = [...ownership.ownerIds]
      .map((ownerId) => snapshot?.equipment[ownerId])
      .find(Boolean);
    const liveRuntime =
      liveFrame
        ? liveOhtRuntimeStore.getActiveTransfer({
            portIds: ownership.portIndexById.keys(),
            stationAliases: ownership.ownerIds,
          })
        : null;

    // Fast path: no live/replay transfer involving this station and no
    // occupancy change key — skip the expensive visual rebuild until needed.
    const hasCandidateTransfer =
      !!liveRuntime?.transfer ||
      (source !== 'live' && !!station?.activeTransfer);
    const occupancyProbeKey =
      source === 'live' && liveFrame
        ? Object.entries(liveFrame.portFoupIds)
            .filter(
              ([portId, ids]) =>
                ids.length > 0 &&
                (ownership.portIndexById.has(portId) ||
                  ownershipMatchesEquipmentId(
                    ownership,
                    equipmentIdFromPortId(portId)
                  ))
            )
            .map(([portId, ids]) => `${portId}:${ids.join('+')}`)
            .sort()
            .join('|')
        : '';
    const processKey = station?.processActive ? '1' : '0';
    const quietKey = `q:${occupancyProbeKey}|${processKey}|${station?.status ?? ''}`;
    if (!hasCandidateTransfer && quietKey === lastKeyRef.current && !presenceAnimatingRef.current) {
      return;
    }

    const transfer = liveRuntime?.transfer
      ? {
          action: liveRuntime.transfer.action,
          portId: liveRuntime.transfer.portId,
          ohtId: liveRuntime.ohtId,
          foupId: liveRuntime.transfer.foupId,
          operationId: liveRuntime.transfer.eventId,
          eventId: liveRuntime.transfer.eventId,
          progress01: liveRuntime.transfer.progress01,
          requestId: undefined,
          handoffX: liveRuntime.x,
          handoffY: liveRuntime.y,
        }
      : source === 'live'
        ? undefined
        : station?.activeTransfer;
    const transferPortId = transfer?.portId;
    // The station was already matched by owner id, so this port is ours; the
    // index just has to survive an id the backend spelled differently.
    const ownedPortIndex =
      resolveOwnedPortIndex(ownership, transferPortId, portCount) ?? undefined;
    const hasExactTransfer =
      !!transfer &&
      !!transfer.ohtId &&
      (transfer.action === 'PICKUP' || transfer.action === 'DROP') &&
      ownedPortIndex !== undefined;

    const occupiedLoadPorts: number[] = [];
    const foupByPortIndex = new Map<number, string>();
    for (const [portId, portIndex] of ownership.portIndexById) {
      // Live equipment must use the scheduler-local ownership ledger. The
      // replay snapshot runs on the global clock and can lag behind a wall-time
      // hold, which used to make a FOUP pop back into a tool after completion.
      const liveFoupIds = liveFrame?.portFoupIds[portId];
      const replayPort = snapshot?.ports[portId];
      const occupiedFoupIds =
        source === 'live' ? liveFoupIds ?? [] : replayPort?.occupiedFoupIds ?? [];
      const occupied =
        occupiedFoupIds.length > 0 ||
        (source !== 'live' && (replayPort?.inventory ?? 0) > 0);
      if (!occupied) continue;
      occupiedLoadPorts.push(portIndex);
      for (const foupId of occupiedFoupIds) {
        if (foupId && !foupByPortIndex.has(portIndex)) {
          foupByPortIndex.set(portIndex, foupId);
        }
      }
    }
    // Overlay-derived tools may have no explicit port array even though the
    // stream publishes standard IDs such as EQ-2-LP-2. Attribute those ports
    // by their encoded equipment id and suffix so post-handoff occupancy stays
    // attached to the same machine that ran the animation.
    if (liveFrame) {
      for (const [portId, foupIds] of Object.entries(liveFrame.portFoupIds)) {
        if (
          foupIds.length === 0 ||
          !ownershipMatchesEquipmentId(ownership, equipmentIdFromPortId(portId))
        ) {
          continue;
        }
        const portIndex = resolveOwnedPortIndex(ownership, portId, portCount);
        if (portIndex == null || occupiedLoadPorts.includes(portIndex)) continue;
        occupiedLoadPorts.push(portIndex);
        const foupId = foupIds[0];
        if (foupId) foupByPortIndex.set(portIndex, foupId);
      }
    }

    type NextPartial = Omit<
      EquipmentPortTransferPresence,
      'foupSlotView' | 'chamberFoups' | 'foupCapacity'
    >;
    let next: NextPartial;
    if (hasExactTransfer) {
      const isPickup = transfer.action === 'PICKUP';
      const portIndex = resolveActiveEquipmentPortIndex(
        isPickup,
        portCount,
        ownedPortIndex
      );
      const foupId =
        transfer.foupId ??
        foupByPortIndex.get(portIndex) ??
        `${transfer.operationId}:FOUP`;
      const progress = Math.min(1, Math.max(0, transfer.progress01));
      // A tool's stop leg carries the track-node id, not the port id, so the
      // port match is only a preference here.
      const stationaryLegs =
        legsByVehicle?.[transfer.ohtId!]?.filter(
          (leg) =>
            (!transfer.requestId || leg.requestId === transfer.requestId) &&
            Math.abs(leg.fromX - leg.toX) < 0.001 &&
            Math.abs(leg.fromY - leg.toY) < 0.001
        ) ?? [];
      const fallbackLeg =
        stationaryLegs.find(
          (leg) =>
            !!transferPortId &&
            (leg.fromId === transferPortId || leg.toId === transferPortId)
        ) ?? stationaryLegs[0];
      const handoffWorld =
        typeof transfer.handoffX === 'number' &&
        typeof transfer.handoffY === 'number'
          ? { x: transfer.handoffX, y: transfer.handoffY }
          : fallbackLeg
            ? { x: fallbackLeg.toX, y: fallbackLeg.toY }
            : null;
      const handoffPose =
        handoffWorld && poses[portIndex]
          ? worldPointToEquipmentKitLocal(
              element,
              handoffWorld,
              poses[portIndex]!.loadPort[1]
            )
          : undefined;
      const portVisuals = resolveEquipmentTransferVisuals({
        isPickup,
        progress,
        portIndex,
        poses,
        handoffPose,
        foupId,
        pathStyle: fabProcessTool ? 'fab-lifter' : 'default',
      }).map((visual) => ({
        ...visual,
        ledStatus:
          visual.portIndex === portIndex
            ? station?.processActive
              ? ('RUNNING' as const)
              : ('IDLE' as const)
            : ('IDLE' as const),
      }));
      const active = portVisuals[portIndex];
      const kitFoupScale = resolveKitFoupScale(width, height, depth);
      const flying =
        active?.foupMode === 'flying' && active.foupPosition
          ? {
              position: active.foupPosition,
              scaleHint: kitFoupScale,
              foupId: active.foupId ?? foupId,
            }
          : null;
      const visiblePorts = portVisuals
        .filter(
          (visual) =>
            visual.foupMode !== 'hidden' &&
            (visual.foupMode === 'resting' ||
              visual.occupancy === 'occupied' ||
              visual.occupancy === 'loading')
        )
        .map((visual) => visual.portIndex);

      const logKey = `${transfer.operationId}|${transfer.eventId}`;
      if (loggedTransferRef.current !== logKey) {
        loggedTransferRef.current = logKey;
        const nodeSource =
          typeof transfer.handoffX === 'number' &&
          typeof transfer.handoffY === 'number'
            ? 'snapshot'
            : fallbackLeg
              ? 'leg'
              : 'MISSING (falling back to tray pose)';
        console.log(
          `[AMHS] ${transfer.operationId}: ${transfer.action} ${foupId} at ` +
            `${element.metadata.name || element.id} / ${transferPortId}`,
          {
            nodeSource,
            handoffWorld,
            handoffPose,
            portPose: poses[portIndex]?.loadPort,
            elementPosition: element.position,
            elementDimensions: element.dimensions,
            elementRotation: element.rotation,
          }
        );
      }

      next = {
        foupPresent: visiblePorts.length > 0 || !!flying,
        occupiedLoadPorts: visiblePorts,
        portVisuals,
        flyingFoup: flying,
        activePortIndex: portIndex,
        isTransferring: !!active?.isLocked,
        transferProgress01: progress,
        transferOperation: isPickup ? 'PICKUP' : 'DROP',
        isProcessing: station?.processActive ?? false,
        equipmentStatus: resolveEquipmentProcessStatus(
          portVisuals,
          station?.processActive ?? false,
          station?.processActive ?? false
        ),
      };
    } else if (occupiedLoadPorts.length > 0 || station?.processActive) {
      let portVisuals = emptyVisuals(portCount);
      for (const portIndex of occupiedLoadPorts) {
        const resting = processingRestVisuals(
          poses,
          portIndex,
          foupByPortIndex.get(portIndex)
        );
        portVisuals = portVisuals.map((visual, index) =>
          index === portIndex ? resting[index]! : visual
        );
      }
      portVisuals = portVisuals.map((visual) => ({
        ...visual,
        ledStatus: station?.processActive ? ('RUNNING' as const) : ('IDLE' as const),
      }));
      next = {
        foupPresent: occupiedLoadPorts.length > 0,
        occupiedLoadPorts,
        portVisuals,
        flyingFoup: null,
        activePortIndex: occupiedLoadPorts[0] ?? null,
        isTransferring: false,
        transferProgress01: null,
        transferOperation: null,
        isProcessing: station?.processActive ?? false,
        equipmentStatus: station?.processActive
          ? 'RUNNING'
          : replayEquipmentStatus(station?.status),
      };
    } else {
      next = {
        ...EMPTY,
        portVisuals: emptyVisuals(portCount),
        transferProgress01: null,
        transferOperation: null,
        equipmentStatus: replayEquipmentStatus(station?.status),
      };
    }

    const animatingFoupId =
      next.flyingFoup?.foupId ??
      (next.isTransferring && hasExactTransfer ? transfer?.foupId : undefined) ??
      null;
    const foupSlotView =
      fabLayout && element
        ? resolveEquipmentFoupSlots({
            ownership,
            portCount,
            capacity: foupCapacity,
            poses,
            layout: fabLayout,
            portVisuals: next.portVisuals,
            source: source === 'live' ? 'live' : 'replay',
            simTimeS,
            toolPickupReady,
            livePortFoupIds: liveFrame?.portFoupIds ?? null,
            replayPorts: snapshot?.ports,
            animatingFoupId,
          })
        : {
            capacity: foupCapacity,
            occupied: next.occupiedLoadPorts.length,
            chamber: [],
            loadPortIndices: next.occupiedLoadPorts,
          };
    const chamberFoups = fabLayout
      ? resolveFabChamberFoups(
          next.portVisuals,
          poses,
          foupSlotView.chamber,
          animatingFoupId
        )
      : [];
    const presence: EquipmentPortTransferPresence = {
      ...next,
      foupSlotView,
      chamberFoups,
      foupCapacity,
      occupiedLoadPorts: foupSlotView.loadPortIndices.length
        ? foupSlotView.loadPortIndices
        : next.occupiedLoadPorts,
      foupPresent:
        foupSlotView.occupied > 0 || !!next.flyingFoup || chamberFoups.length > 0,
    };

    const key = [
      presence.equipmentStatus,
      presence.isProcessing ? '1' : '0',
      presence.activePortIndex,
      presence.foupCapacity,
      foupSlotView.chamber.map((s) => `${s.slotIndex}:${s.foupId}`).join(','),
      presence.flyingFoup?.position.map((n) => n.toFixed(3)).join(',') ?? '',
      ...presence.portVisuals.map(
        (v) =>
          `${v.shutterOpen.toFixed(2)}:${v.foupMode}:${v.occupancy}:${v.transferState}:${v.ledStatus ?? ''}:${v.foupId ?? ''}`
      ),
    ].join('|');

    const animating =
      presence.isTransferring ||
      presence.portVisuals.some(
        (v) =>
          v.foupMode === 'flying' ||
          v.shutterState === 'opening' ||
          v.shutterState === 'closing'
      );
    presenceAnimatingRef.current = animating;

    if (animating || key !== lastKeyRef.current) {
      lastKeyRef.current = animating ? key : (hasCandidateTransfer ? key : quietKey || key);
      setPresence(presence);
    }
  });

  return presence;
}

/**
 * Backward-compatible FOUP presence (visibility only).
 * Prefer {@link useEquipmentPortTransfer} for shutter + path animation.
 */
export function useEquipmentFoupTransfer(
  element?: LayoutElement | null
): EquipmentFoupPresence {
  const full = useEquipmentPortTransfer({
    element,
    width: element?.dimensions.width ?? 2,
    height: 2.4,
    depth: element?.dimensions.height ?? 1.8,
    loadPortCount: 2,
  });
  return {
    foupPresent: full.foupPresent,
    occupiedLoadPorts: full.occupiedLoadPorts,
  };
}
